/**
 * Pumpkin painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pumpkinPaintingIdeasPack: ToilePack = {
    slug: 'pumpkin-painting-ideas',
    category: 'craft',
    name: 'Pumpkin painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'delft', label: 'Blue florals', subject: 'a white pumpkin painted with blue and white Delft-style flowers and vines'},
        {key: 'cat', label: 'Black cat', subject: 'an orange pumpkin painted with a smiling black cat face and green eyes'},
        {key: 'minis', label: 'Gold minis', subject: 'a stack of mini pumpkins painted in gold, cream and blush stripes and dots'},
    ],
    styles: [
        {key: 'acrylic', label: 'Hand-brushed', style: 'brushed by hand in smooth satin acrylic', palette: "Clean painted colour over the pumpkin's own skin, the stem left natural green-brown.", framing: 'Sitting alone on the step, shot at eye level, close and centred.'},
        {key: 'chalk', label: 'Chalk paint', style: 'in matte farmhouse chalk paint, lightly distressed', palette: 'Chalky muted tones, the porch boards grey and the background autumn leaves soft.', framing: 'Grouped with a small straw bale, a potted mum and a folded plaid blanket, shot slightly from above.'},
        {key: 'pens', label: 'Paint pens', style: 'detailed with fine paint-pen lines and raised acrylic dots', palette: 'Crisp contrasting detail in white, black and metallic over the base colour.', framing: 'Beside a front door with a coir doormat and a wicker basket of gourds, shot at eye level.'},
    ],
    directions: ['teal pumpkin', 'sunflowers', 'polka dots'],
    directionHint: 'try: teal pumpkin',
    seo: {
        title: 'Pumpkin Painting Ideas: Blue Florals, Black Cat, Gold Minis',
        metaDescription: 'Nine painted pumpkin ideas for a porch: blue florals on white, a black cat on orange and gold minis, in three paint finishes.',
        h1: 'Pumpkin painting ideas: blue florals, black cat, gold minis',
        tagline: 'Nine painted pumpkins, three designs, each shown on a porch step in smooth acrylic, chalk paint or fine paint-pen detail.',
        intro: 'Painting pumpkins instead of carving them is the autumn porch project, for families and for anyone who wants them to last. These nine show three designs: a white pumpkin with blue and white Delft-style flowers and vines, an orange pumpkin painted as a smiling black cat with green eyes, and a stack of mini pumpkins in gold, cream and blush stripes and dots. Type another design, and the same porch and paint finishes carry it.',
        useCases: [
            'an autumn porch display',
            'a school fall party',
            'a centerpiece that keeps',
            'a family craft evening',
        ],
        faq: [
            {q: 'Do painted pumpkins last longer than carved ones?', a: 'A painted pumpkin is not carved, so it keeps its shape longer than a carved one. The pictures show uncarved pumpkins.'},
            {q: 'What are the three finishes?', a: 'Smooth satin acrylic brushed by hand, matte chalk paint lightly distressed, and fine paint-pen lines with raised acrylic dots.'},
            {q: 'Is the stem painted too?', a: 'In the hand-brushed picture the stem stays its natural green-brown, and the paint covers the rest of the skin.'},
            {q: 'Can I change the design?', a: 'Yes. Type another design and the three finishes carry it.'},
        ],
    },
}
